import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { LayoutDashboard, FilePlus, LogOut, Users, Menu, X, AlertCircle } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);

  if (!user) return null;

  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === 'admin';

  const handleLogout = () => {
    setLogoutConfirm(false);
    setMobileOpen(false);
    logout();
  };

  const navItems = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
    { to: '/challan/new', label: 'New Challan', icon: FilePlus, testId: 'nav-new-challan' },
    ...(isAdmin ? [{ to: '/officers', label: 'Officers', icon: Users, testId: 'nav-officers' }] : [])
  ];

  return (
    <>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 group" data-testid="header-brand">
              <img src="/favicon.png" alt="MuniCop" className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
              <span className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
                MuniCop
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1" data-testid="header-nav-desktop">
              {navItems.map(item => (
                <Link key={item.to} to={item.to}>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={item.testId}
                    className={`text-sm font-medium transition-all duration-200 ${
                      isActive(item.to) ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50/50'
                    }`}
                  >
                    <item.icon className="w-4 h-4 mr-1.5" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLogoutConfirm(true)}
                data-testid="logout-btn"
                className="text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>

            <button
              className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white animate-fade-in" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-1">
              {navItems.map(item => (
                <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)}>
                  <div
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                      isActive(item.to) ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`mobile-${item.testId}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </div>
                </Link>
              ))}
              <div className="border-t border-slate-100 pt-2 mt-2">
                <div className="flex items-center justify-between px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLogoutConfirm(true)}
                    data-testid="mobile-logout-btn"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-1.5" />
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutConfirm} onOpenChange={setLogoutConfirm}>
        <DialogContent data-testid="logout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Confirm Logout
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to logout? Any unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setLogoutConfirm(false)} data-testid="cancel-logout-btn">
              Cancel
            </Button>
            <Button onClick={handleLogout} data-testid="confirm-logout-btn" className="bg-red-600 hover:bg-red-700 text-white">
              <LogOut className="w-4 h-4 mr-1.5" />
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
